import type {ReactElement} from 'react';
import React, {useMemo, useRef, useState} from 'react';
import type {
  AiTextReWriteOptionProps,
  AiTextReWritePromptFilter,
  AiTextReWritePromptFilterId,
  SelectedPromptFilter,
} from '@Components/poster-editor/components/poster-editing-side-panel/components/ai-text-re-write-option/ai-text-re-write-option.types';
import {AI_TEXT_RE_WRITE_PROMPT_FILTERS} from '@Components/poster-editor/components/poster-editing-side-panel/components/ai-text-re-write-option/ai-text-re-write-option.types';
import {ContextualMenuButtonOption} from '@Components/contextual-menu/components/contextual-menu-button-option';
import {type ButtonItemsStorageType, ContextualButtonItemType} from '@Components/contextual-menu/components/button-items';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {Size} from '@Components/button';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {Tag, TagType} from '@Components/tag';
import {InputFieldState, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {InputField} from '@Components/input-field-v2';
import styles from './ai-text-re-write-option.module.scss';
import {noop} from '@Utils/general.util';
import {AiTextPromptBox} from '@Components/ai-text-prompt-box';
import {AiPromptBoxVariant, PromptBoxConfigOptionType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiButton} from '@Components/ai-button';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {Text, TextSize, TextType} from '@Components/text';
import {useLeftRightScrollShadow} from '@Hooks/useLeftRightScrollShadow';
import {useAppSelector} from '@/hooks';
import {getImageBackgrounds, getImageItems, getTextItems} from '@Components/poster-editor/poster-editor-reducer';
import {initiatePosterMorphLoading, terminatePosterMorphLoading} from '@PosterWhiteboard/poster/poster-init';
import {morphPoster} from '@Libraries/ai-text-design-morph.library';
import {IconSize} from '@Components/icon-v2/icon.types';
import {getIconForFilterId} from '@Components/poster-editor/components/poster-editing-side-panel/components/ai-text-re-write-option/ai-text-re-write-option.library';
import {ToggleSwitch} from '@Components/toggle-switch-v2';
import {Icon} from '@Components/icon-v2';

export function AiTextReWriteOption({showNewTag = false, ...props}: AiTextReWriteOptionProps): ReactElement<any> | null {
  const id = props.id ?? 'ai-text-re-write-option-default-id';
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());
  const [isDrawerOpened, setIsDrawerOpened] = useState(false);
  const [prompt, setPrompt] = useState<string | undefined>(undefined);
  const [selectedPromptFilters, setSelectedPromptFilters] = useState<SelectedPromptFilter[]>([]);
  const [openedFilterId, setOpenedFilterId] = useState<AiTextReWritePromptFilterId | undefined>(undefined);
  const [includeImages, setIncludeImages] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const promptFilterContainerRef = useRef<HTMLUListElement>(null);
  const {leftShadow, rightShadow} = useLeftRightScrollShadow(promptFilterContainerRef, !!promptFilterContainerRef);
  const inputRef = useRef<HTMLInputElement>(null);
  const fitlers = useMemo(() => {
    return props.promptFilter && props.promptFilter.length > 0 ? props.promptFilter : AI_TEXT_RE_WRITE_PROMPT_FILTERS;
  }, [props.promptFilter]);
  const textItems = useAppSelector(getTextItems);

  const onSelectorClick = (_: string): void => {
    setIsDrawerOpened((prevState) => {
      return !prevState;
    });
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  };

  const buttonItem: ButtonItemsStorageType = [
    {
      isLoading: false,
      isDisabled: false,
      type: ContextualButtonItemType.AI_BUTTON,
      id: `${id}-cta-selector`,
      variant: isPremium.current ? AIButtonVariant.SUBDUED : AIButtonVariant.PREMIUM_SUBDUED,
      buttonSize: Size.XSMALL,
      buttonIcon: 'icon-ai-caption',
      buttonText: window.i18next.t('pmwjs_ai_writer'),
      loadingStateText: window.i18next.t('pmwjs_generating'),
      onClick: onSelectorClick,
      className: isDrawerOpened ? styles.selectedSelector : styles.defaultSelector,
    },
  ];

  const isPromptInputDisabled = (): boolean => {
    return isLoading;
  };

  const onPromptSubmit = (): void => {
    setPrompt((prevState) => {
      return prevState?.trim();
    });

    if (prompt && prompt.trim().length > 0) {
      const loadingKey = `${id}-poster-morph-key`;

      initiatePosterMorphLoading(loadingKey);
      setIsLoading(true);

      const filterMap = selectedPromptFilters.reduce<Record<string, string>>((acc, selection) => {
        acc[selection.filterId] = selection.subFilterId;
        return acc;
      }, {});

      const hashedId = window.posterEditor?.whiteboard?.hashedID;

      if (hashedId) {
        morphPoster(hashedId, prompt, filterMap, includeImages)
          .then(() => {
            terminatePosterMorphLoading(loadingKey);
            setIsLoading(false);
          })
          .catch((e) => {
            console.error(e);
            terminatePosterMorphLoading(loadingKey);
            setIsLoading(false);
          });
      }
    }
  };

  const getFiltersCarousel = (): ReactElement<any> => {
    return (
      <ul
        ref={promptFilterContainerRef}
        className={`_position-relative flex-row-align-center _unmargin ${styles.filterList} ${rightShadow ? styles.rightShadow : ''} ${leftShadow ? styles.leftShadow : ''}`}
      >
        {fitlers.map((filterItem) => {
          const selection = selectedPromptFilters.find((item) => {
            return filterItem.id === item.filterId;
          });
          const selectedSubfilter = selection
            ? filterItem.subfilters.find((subFilters) => {
                return subFilters.id === selection.subFilterId;
              })
            : undefined;
          const isOpened = openedFilterId === filterItem.id;
          return (
            <li
              key={filterItem.id}
              className={`_position-relative flexbox _unpadded`}
              onClick={(e) => {
                e.stopPropagation();
                setOpenedFilterId(isOpened ? undefined : filterItem.id);
              }}
            >
              <PillButton
                isLoading={isLoading}
                leftIconProps={{icon: getIconForFilterId(filterItem.id), size: IconSize.SIZE_ICON_16}}
                onClose={(event) => {
                  event.stopPropagation();
                  setSelectedPromptFilters((prevState) => {
                    return prevState.filter((prevSelection) => {
                      return prevSelection.filterId !== filterItem.id;
                    });
                  });
                }}
                size={PillButtonSize.SMALL}
                text={selectedSubfilter ? selectedSubfilter.name : filterItem.name}
                onClick={noop}
                isSelected={!!selection}
              />
              {isOpened && !isLoading ? getPopoverForFilterItem(filterItem) : null}
            </li>
          );
        })}
      </ul>
    );
  };

  const getPopoverForFilterItem = (filter: AiTextReWritePromptFilter): ReactElement<any> => {
    return (
      <ul className={`_unmargin _position-absolute spacing-p-3 border-s-standard radius-4 shadow-floating-elevation-3 spacing-p-3 flex-h-row flex-wrap ${styles.filterPopover}`}>
        {filter.subfilters.map((subfilterItem) => {
          const isSelected = selectedPromptFilters.find((selection) => {
            if (filter.id === selection.filterId) {
              return selection.subFilterId === subfilterItem.id;
            }
            return false;
          });
          return (
            <li
              key={subfilterItem.id}
              className={`spacing-p-t-2 spacing-p-b-2 spacing-p-r-3 spacing-p-l-3 radius-round _cursor-pointer ${styles.subfilterItem} ${isSelected ? styles.selectedSubfilterItem : ''}`}
              onClick={() => {
                setSelectedPromptFilters((prevState) => {
                  setOpenedFilterId(undefined);
                  return [
                    ...prevState.filter((prevItem) => {
                      return prevItem.filterId !== filter.id;
                    }),
                    {filterId: filter.id, subFilterId: subfilterItem.id}
                  ];
                });
              }}
            >
              <Text bold type={TextType.BODY} size={TextSize.XXSMALL} val={subfilterItem.name} className={isSelected ? 'content-primary' : 'content-body'} />
            </li>
          );
        })}
      </ul>
    );
  };

  const getImageReplaceToggle = (): ReactElement<any> => {
    return (
      <div className={'flex-row-justify-between'}>
        <div className={`flex-h-row flex-center ${styles.toggleInfo}`}>
          <Icon icon={'icon-image'} size={IconSize.SIZE_ICON_16} />
          <Text val={'Update images'} type={TextType.BODY} size={TextSize.XSMALL} />
        </div>
        <ToggleSwitch
          isChecked={includeImages}
          onChange={() => {
            setIncludeImages((prevState) => {
              return !prevState;
            });
          }}
        />
      </div>
    );
  };

  const getDrawerContent = (): ReactElement<any> | null => {
    if (isDrawerOpened) {
      // this needs to be a dumb reusable component
      return (
        <div className={`flex-v-row ${styles.optionContainer}`}>
          <InputField
            maxLength={1000}
            isControlled
            isMultiline
            inputType={InputFieldType.SEARCH}
            input={prompt}
            onInputChange={(updatedTerm: string): void => {
              setPrompt(updatedTerm);
            }}
            onInputSubmit={onPromptSubmit}
            isDisabled={isPromptInputDisabled()}
            placeholder={window.i18next.t('pmwjs_describe_design_content')}
            ref={inputRef}
            state={InputFieldState.DEFAULT}
          />
          {getFiltersCarousel()}
          {getImageReplaceToggle()}
          <AiButton isLoading={isLoading} isFullWidth icon={'icon-ai-caption'} text={'Update content'} size={Size.XSMALL} onClick={onPromptSubmit} />
        </div>
      );
    }
    return null;
  };
  const getNewTag = (): ReactElement<any> | undefined => {
    return showNewTag ? (
      <Tag className={'radius-round spacing-p-l-2 spacing-p-r-2 spacing-p-t-1 spacing-p-b-1'} text={window.i18next.t('pmwjs_new')} type={TagType.SECONDARY} />
    ) : undefined;
  };

  const getAiTextPromptBox = (): ReactElement<any> | null => {
    return isDrawerOpened ? (
      <AiTextPromptBox
        isInMobileMode
        onPromptChange={setPrompt}
        onGenerationOptionChange={(change) => {
          console.log(change);
        }}
        mainPlaceholder={window.window.i18next.t('pmwjs_describe_email_content')}
        variant={AiPromptBoxVariant.MINIATURE}
        optionsToUse={[PromptBoxConfigOptionType.TONE]}
        rewriteContent={async (_, __) => {
          console.log(_, __);
        }}
      />
    ) : null;
  };

  return textItems.length > 0 && isPremium.current && window.PMW.currentUserOnPageLoad?.isSuper ? (
    <ContextualMenuButtonOption
      subOptionHasOverflow={isDrawerOpened}
      subOptionClassName={styles.container}
      isInlineOption
      id={id}
      data={buttonItem}
      tag={getNewTag()}
      label={window.i18next.t('pmwjs_content')}
    >
      {getDrawerContent()}
    </ContextualMenuButtonOption>
  ) : null;
}
